import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dt, inr } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";

const STATUS = { pending: "bg-warning/15 text-warning", approved: "bg-success/15 text-success", rejected: "bg-destructive/15 text-destructive" };

export default function BankAccountRequests() {
  const { user } = useAuth();
  const isMerchant = user.role === "merchant";
  const canReview = ["super_admin", "admin"].includes(user.role);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ vpa: "", payee_name: "", provider: "", bank_label: "UPI", daily_limit: "", bank_type: "", legal_form: "", owner_name: "", account_number: "", linked_phone: "", linked_email: "", forward_otp_to: "", deposit_min: "", deposit_max: "", daily_max_count: "", monthly_max: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/bank-account-requests").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const num = (v) => (v === "" ? null : parseFloat(v));
      await api.post("/bank-account-requests", {
        vpa: form.vpa, payee_name: form.payee_name, provider: form.provider || form.bank_label,
        bank_label: form.bank_label, daily_limit: parseFloat(form.daily_limit) || 0,
        bank_type: form.bank_type || null, legal_form: form.legal_form || null, owner_name: form.owner_name || null,
        account_number: form.account_number || null, linked_phone: form.linked_phone || null,
        linked_email: form.linked_email || null, forward_otp_to: form.forward_otp_to || null,
        deposit_min: num(form.deposit_min), deposit_max: num(form.deposit_max),
        daily_max_count: form.daily_max_count === "" ? null : parseInt(form.daily_max_count, 10),
        monthly_max: num(form.monthly_max),
      });
      toast.success("Request submitted for review");
      setOpen(false); setForm({ vpa: "", payee_name: "", provider: "", bank_label: "UPI", daily_limit: "", bank_type: "", legal_form: "", owner_name: "", account_number: "", linked_phone: "", linked_email: "", forward_otp_to: "", deposit_min: "", deposit_max: "", daily_max_count: "", monthly_max: "" });
      load();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const review = async (id, action) => {
    try {
      await api.patch(`/bank-account-requests/${id}`, { action });
      toast.success(`Request ${action}d`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">{canReview ? "Review bank account requests" : "Bank account requests"}</h2>
          <p className="text-sm text-muted-foreground">New UPI IDs are added to rotation only after approval.</p>
        </div>
        {isMerchant && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-bank-request-button"><Plus className="mr-1.5 h-4 w-4" /> Add new bank account</Button></DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Request a new bank account</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>UPI ID (VPA)</Label><Input required value={form.vpa} onChange={(e) => setForm({ ...form, vpa: e.target.value })} placeholder="store@okhdfcbank" data-testid="req-vpa-input" /></div>
                  <div className="space-y-1.5"><Label>Payee name</Label><Input required value={form.payee_name} onChange={(e) => setForm({ ...form, payee_name: e.target.value })} placeholder="My Store" data-testid="req-payee-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Bank provider</Label><Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="KKBROTHERS" data-testid="req-provider-input" /></div>
                  <div className="space-y-1.5"><Label>Bank type</Label><Input value={form.bank_type} onChange={(e) => setForm({ ...form, bank_type: e.target.value })} placeholder="GPAYMANAGERACC" data-testid="req-banktype-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Legal form</Label><Input value={form.legal_form} onChange={(e) => setForm({ ...form, legal_form: e.target.value })} placeholder="CURRENT (BUSINESS)" data-testid="req-legalform-input" /></div>
                  <div className="space-y-1.5"><Label>Owner name</Label><Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} placeholder="Pal Industry" data-testid="req-owner-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Account number</Label><Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} data-testid="req-accnum-input" /></div>
                  <div className="space-y-1.5"><Label>Linked phone</Label><Input value={form.linked_phone} onChange={(e) => setForm({ ...form, linked_phone: e.target.value })} data-testid="req-phone-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Linked E-mail</Label><Input value={form.linked_email} onChange={(e) => setForm({ ...form, linked_email: e.target.value })} data-testid="req-email-input" /></div>
                  <div className="space-y-1.5"><Label>Forward OTP email to</Label><Input value={form.forward_otp_to} onChange={(e) => setForm({ ...form, forward_otp_to: e.target.value })} data-testid="req-otp-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Daily max SUM (₹, 0=∞)</Label><Input type="number" min="0" value={form.daily_limit} onChange={(e) => setForm({ ...form, daily_limit: e.target.value })} placeholder="100000" data-testid="req-limit-input" /></div>
                  <div className="space-y-1.5"><Label>Daily max COUNT</Label><Input type="number" min="0" value={form.daily_max_count} onChange={(e) => setForm({ ...form, daily_max_count: e.target.value })} placeholder="100" data-testid="req-maxcount-input" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5"><Label>Deposit min</Label><Input type="number" min="0" value={form.deposit_min} onChange={(e) => setForm({ ...form, deposit_min: e.target.value })} placeholder="300" data-testid="req-depmin-input" /></div>
                  <div className="space-y-1.5"><Label>Deposit max</Label><Input type="number" min="0" value={form.deposit_max} onChange={(e) => setForm({ ...form, deposit_max: e.target.value })} placeholder="100000" data-testid="req-depmax-input" /></div>
                  <div className="space-y-1.5"><Label>Monthly max</Label><Input type="number" min="0" value={form.monthly_max} onChange={(e) => setForm({ ...form, monthly_max: e.target.value })} data-testid="req-monthlymax-input" /></div>
                </div>
                <DialogFooter><Button type="submit" disabled={saving} data-testid="req-submit-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit request"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <Card className="p-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Bank provider</TableHead>
                <TableHead>UPI ID / Payee</TableHead>
                {canReview && <TableHead>Merchant</TableHead>}
                <TableHead className="text-right">Daily limit</TableHead>
                <TableHead>Status</TableHead>
                {canReview && <TableHead className="text-right">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows === null && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {rows && rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No requests yet.</TableCell></TableRow>}
              {rows && rows.map((r) => (
                <TableRow key={r.id} data-testid={`bank-request-row-${r.id}`}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{dt(r.created_at)}</TableCell>
                  <TableCell className="font-semibold">{r.provider}</TableCell>
                  <TableCell><div className="font-mono text-xs">{r.vpa}</div><div className="text-xs text-muted-foreground">{r.payee_name}</div></TableCell>
                  {canReview && <TableCell className="text-sm">{r.merchant_name}</TableCell>}
                  <TableCell className="text-right font-mono text-xs">{r.daily_limit ? inr(r.daily_limit) : "∞"}</TableCell>
                  <TableCell><Badge className={STATUS[r.status]}>{r.status}</Badge>{r.reviewed_by_name && <div className="text-[11px] text-muted-foreground mt-0.5">by {r.reviewed_by_name}</div>}</TableCell>
                  {canReview && (
                    <TableCell className="text-right">
                      {r.status === "pending" ? (
                        <div className="flex justify-end gap-1.5">
                          <Button size="icon" variant="outline" className="h-8 w-8 text-success" onClick={() => review(r.id, "approve")} data-testid={`approve-bank-${r.id}`}><Check className="h-4 w-4" /></Button>
                          <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" onClick={() => review(r.id, "reject")} data-testid={`reject-bank-${r.id}`}><X className="h-4 w-4" /></Button>
                        </div>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
